/**
 * NBPLCSDGContribution — SDG view
 * Route: /corporate/nbplc/sdg
 */
import React, { useEffect, useState } from "react";
import { useNBPLC } from "@/context/NBPLCContext";
import { PageHeader, SectionCard } from "@/components/ds";
import client from "@/api/client";

const SDG_COLORS = {6:"#26BDE2",7:"#FCC30B",9:"#F36D25",12:"#BF8B2E",13:"#3F7E44"};
const SDG_NAMES  = {6:"Clean Water & Sanitation",7:"Affordable & Clean Energy",9:"Industry & Innovation",12:"Responsible Consumption",13:"Climate Action"};

export default function NBPLCSDGContribution() {
  const { selectedPeriod, period, periods, setSelectedPeriod } = useNBPLC();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedPeriod) return;
    setLoading(true);
    client.get(`/nbplc/sdg/${selectedPeriod}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [selectedPeriod]);

  if (loading && !data) return (
    <div className="p-6 space-y-4">{[1,2,3].map(i=><div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
  );

  const sdgs = data?.by_sdg || [];

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Nigeria Breweries Plc</p>
          <PageHeader title="SDG Contribution" />
          <p className="text-xs text-slate-400 mt-0.5">{period?.label}</p>
        </div>
        <select value={selectedPeriod||""} onChange={e=>setSelectedPeriod(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white">
          {periods.map(p=><option key={p.period_id} value={p.period_id}>{p.label}</option>)}
        </select>
      </div>

      {!data?.data_available ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-700">
          No SDG data for this period. Visit Monthly Reporting to calculate data.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[6,7,9,12,13].map(sdg => {
              const s = sdgs.find(x=>x.sdg===sdg);
              const color = SDG_COLORS[sdg]||"#666";
              const val = s ? Math.round(s.total_annualised_tco2e/1000) : 0;
              const state = s ? (s.total_annualised_tco2e > 0 ? "Quantified" : "Active") : "Not Assessed";
              const stateCls = state==="Quantified"?"bg-emerald-100 text-emerald-700":"Active"?"bg-blue-100 text-blue-700":"bg-slate-100 text-slate-500";
              return (
                <div key={sdg} className="bg-white border border-slate-200 rounded-xl p-4"
                  style={{borderTop:`3px solid ${color}`}}>
                  <p className="text-[9px] font-bold uppercase tracking-wide mb-1" style={{color}}>SDG {sdg}</p>
                  <p className="text-base font-bold text-slate-900">{val > 0 ? `${val}K tCO₂e` : state}</p>
                  <p className="text-[9px] text-slate-400 mt-1">{SDG_NAMES[sdg]}</p>
                  <span className={`inline-block mt-1.5 text-[8px] font-bold px-1.5 py-0.5 rounded ${stateCls}`}>{state}</span>
                </div>
              );
            })}
          </div>

          <SectionCard>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Detail by SDG</p>
            <table className="w-full text-xs">
              <thead><tr className="border-b border-slate-100">
                {["SDG","Goal","Type","tCO₂e/yr","Source Metric","State"].map(h=>(
                  <th key={h} className="text-left text-[9px] text-slate-400 pb-2 pr-3 uppercase tracking-wide">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {sdgs.map(s => {
                  const color = SDG_COLORS[s.sdg]||"#666";
                  const contrib = s.all_contributions?.[0] || {};
                  const metric = typeof contrib.source_metrics === "object" && !Array.isArray(contrib.source_metrics)
                    ? contrib.source_metrics?.primary || "Monthly data"
                    : (contrib.source_metrics?.[0] || "Monthly data");
                  const state = s.total_annualised_tco2e > 0 ? "Quantified" : "Active";
                  return (
                    <tr key={s.sdg} className="border-b border-slate-50">
                      <td className="py-2 pr-3">
                        <span className="text-white text-[9px] font-bold px-2 py-0.5 rounded" style={{background:color}}>SDG {s.sdg}</span>
                      </td>
                      <td className="py-2 pr-3 text-slate-700">{s.name}</td>
                      <td className="py-2 pr-3 text-slate-500">{s.type}</td>
                      <td className="py-2 pr-3 font-bold text-emerald-700 tabular-nums">
                        {s.total_annualised_tco2e >= 1000 ? `${Math.round(s.total_annualised_tco2e/1000)}K` : Math.round(s.total_annualised_tco2e) || "—"}
                      </td>
                      <td className="py-2 pr-3 text-slate-400 text-[9px]">{String(metric).slice(0,40)}</td>
                      <td className="py-2">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${state==="Quantified"?"bg-emerald-100 text-emerald-700":"bg-blue-100 text-blue-700"}`}>{state}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[9px] text-slate-400 italic mt-3">
              SDG indicators derived from operational data and require external validation for formal disclosure.
            </p>
          </SectionCard>
        </>
      )}
    </div>
  );
}
